import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, FormField, Input, Multiselect, Select } from '@amzn/awsui-components-react';
import { SelectProps } from '@amzn/awsui-components-react/polaris/select';
import Autosuggest, { AutosuggestProps } from '@amzn/awsui-components-react/polaris/autosuggest';
import { useLocalizedText } from '../../hooks/localizedText';
import { useTOptions } from '../../hooks/localization/tOptions';
import { DAYS_OF_WEEK } from '../../data/eventOptionLists';
import { useLocale } from '../../hooks/locale';
import { buildTimeOptions } from '../../utils/timeOptions';
import { useSharedFilters, optionsToValueArrayString } from '../../hooks/betterEvents/filters';
import { AbsoluteDatePicker } from '../absoluteDatePicker';
import { getDateRangeFromStartAndEnd } from '../../utils/url';
import { publishKatalMetric } from '../katalAnalytics';
import { autocomplete, getPlace } from '../../clients/amazonLocationClient';

/**
 * Maps a "Shift length" option value ("0-1", "1-3", "3-5", "5-999") to the min/max URL params.
 * Both bounds are always returned — the 0 (no lower bound) and 999 (no upper bound) sentinels are
 * kept rather than blanked, because isValidShiftDuration only filters when BOTH activity_length_min
 * and activity_length_max are set. Exported so each option can be regression-tested (CR-291797692).
 */
export function shiftLengthValueToBounds(value: string): { min: string; max: string } {
  const [min, max] = (value || '').split('-');
  return { min: min ?? '', max: max ?? '' };
}

/**
 * Counts how many of the "more filters" (the secondary set hidden behind Show more) are active,
 * so the collapsed toggle can surface a count and an applied-but-hidden filter isn't invisible
 * (CR-291797692 rev 8). Shift length is one logical filter even though it spans two URL params
 * (activity_length_min/_max). days_of_week is considered empty when unset or the literal "[]".
 * Typed loosely (Partial record of string) so it can be unit-tested without the full hook state.
 */
export function countActiveSecondaryFilters(filterValues: {
  days_of_week?: string;
  spots?: string;
  activity_length_min?: string;
  activity_length_max?: string;
  start_time?: string;
  end_time?: string;
}): number {
  return [
    filterValues.days_of_week && filterValues.days_of_week !== '[]',
    filterValues.spots,
    filterValues.activity_length_min || filterValues.activity_length_max,
    filterValues.start_time,
    filterValues.end_time,
  ].filter(Boolean).length;
}

/**
 * Top-aligned search filters for the new navigation layout.
 * Matches the ambassador directory page filter pattern.
 * Filters apply immediately on change (no Apply button).
 */
export function TopSearchFilters() {
  const { t } = useLocalizedText(['translation']);
  const locale = useLocale();
  const {
    filterValues,
    updateFilter,
    updateFilters,
    resetFilters: resetFiltersBase,
    applyFilters,
  } = useSharedFilters();

  const [locationLabel, setLocationLabel] = useState(filterValues.volunteer_location_label);
  const [locationOptions, setLocationOptions] = useState<AutosuggestProps.Options>([]);
  const [locationStatus, setLocationStatus] = useState<AutosuggestProps.StatusType>();
  const placeIdMap = useRef(new Map<string, string>());
  const debounceTimer = useRef<ReturnType<typeof setTimeout>>();
  const searchDebounceTimer = useRef<ReturnType<typeof setTimeout>>();
  const applyFiltersRef = useRef(applyFilters);
  // Keep applyFilters ref current so the debounce timer closure always calls the latest version
  applyFiltersRef.current = applyFilters;

  // Sync locationLabel with filter values
  useEffect(() => {
    setLocationLabel(filterValues.volunteer_location_label);
  }, [filterValues.volunteer_location_label]);

  useEffect(() => {
    return () => {
      clearTimeout(debounceTimer.current);
      clearTimeout(searchDebounceTimer.current);
    };
  }, []);

  // Non-query filters auto-apply to URL immediately on change.
  // Query (keyword) is excluded here because it has its own 700ms debounce to avoid
  // firing a search on every keystroke.
  //
  // The didApplyOnce ref skips the mount run. filterValues are derived from the URL, so on mount
  // there is nothing to apply — but applyFilters -> updateParams unconditionally deletes the
  // `page` param and re-emits a filter_* metric for every active filter. Without this guard,
  // opening a bookmarked URL like /?query=food&page=3 silently jumped back to page 1 and
  // inflated filter metrics on every page load.
  const didApplyOnce = useRef(false);
  useEffect(() => {
    if (!didApplyOnce.current) {
      didApplyOnce.current = true;
      return;
    }
    applyFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filterValues.start_timestamp,
    filterValues.end_timestamp,
    filterValues.days_of_week,
    filterValues.activity_location,
    filterValues.volunteer_location,
    filterValues.spots,
    filterValues.activity_length_min,
    filterValues.activity_length_max,
    filterValues.start_time,
    filterValues.end_time,
  ]);

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  const applyImmediately = useCallback(() => {
    /* no-op: filters auto-apply via useEffect */
  }, []);

  const handleLocationLoadItems = useCallback((filteringText: string) => {
    if (filteringText.length < 2) {
      setLocationOptions([]);
      setLocationStatus(undefined);
      return;
    }
    clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(async () => {
      try {
        setLocationStatus('loading');
        setLocationOptions([]);
        const response = await autocomplete({ QueryText: filteringText, MaxResults: 5 });
        placeIdMap.current.clear();
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const options = ((response as any).ResultItems ?? []).map((item: any) => {
          const title = item.Title ?? '';
          if (item.PlaceId) placeIdMap.current.set(title, item.PlaceId);
          return { value: title, label: title };
        });
        setLocationOptions(options);
        setLocationStatus('finished');
      } catch {
        setLocationStatus('error');
      }
    }, 300);
  }, []);

  const handleLocationSelect = useCallback(
    async (value: string) => {
      const placeId = placeIdMap.current.get(value);
      if (!placeId) return;
      try {
        const response = await getPlace({ PlaceId: placeId });
        if (response.Position) {
          const lat = response.Position[1];
          const lng = response.Position[0];
          updateFilters({ volunteer_location: `${lat},${lng}`, volunteer_location_label: value });
          applyImmediately();
        }
      } catch {
        setLocationLabel('');
        updateFilters({ volunteer_location: '', volunteer_location_label: '' });
      }
    },
    [updateFilters, applyImmediately]
  );

  const resetFilters = () => {
    setLocationLabel('');
    setLocationOptions([]);
    setLocationStatus(undefined);
    placeIdMap.current.clear();
    resetFiltersBase();
    publishKatalMetric('Click', 'filter_resetFilters');
  };

  const daysOfWeekOptions = useTOptions(['translation'], DAYS_OF_WEEK);
  // Half-hourly time-of-day options, built at runtime with HH:MM values (cce-7184).
  const timeOptions = useMemo(() => buildTimeOptions(locale, 30), [locale]);
  const startTimeOptions = timeOptions;
  const endTimeOptions = timeOptions;
  const shiftLengthOptions: SelectProps.Options = [
    { label: t('topFilters.shiftLengthUnder1'), value: '0-1' },
    { label: t('topFilters.shiftLength1to3'), value: '1-3' },
    { label: t('topFilters.shiftLength3to5'), value: '3-5' },
    { label: t('topFilters.shiftLengthOver5'), value: '5-999' },
  ];

  const activityTypeOptions: SelectProps.Options = [
    { value: 'all', label: t('advancedSearch.allType') },
    { value: 'in-person', label: t('advancedSearch.physicalType') },
    { value: 'virtual', label: t('advancedSearch.virtualType') },
  ];

  const selectedDaysOfWeek = filterValues.days_of_week
    ? filterValues.days_of_week
        .replace(/^\[|\]$/g, '') // strip surrounding brackets
        .split(',')
        .filter((v) => v !== '')
        .map((v) => {
          const option = (daysOfWeekOptions as SelectProps.Option[]).find((o) => o.value === v);
          return { value: v, label: option?.label || v };
        })
    : [];

  const [showMoreFilters, setShowMoreFilters] = useState(false);

  // The "more filters" section is only visually collapsed (maxHeight: 0 + overflow: hidden) so the
  // max-height transition can animate; display:none/hidden would kill the animation. But clipping
  // alone leaves the controls inside in the tab order and accessibility tree, so a keyboard or
  // screen-reader user could Tab into invisible filters. Setting the `inert` DOM property removes
  // the descendants from both while preserving the animation. `inert` isn't typed as a JSX prop in
  // @types/react 18, so we toggle the property imperatively via a ref.
  const moreFiltersRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (moreFiltersRef.current) {
      moreFiltersRef.current.inert = !showMoreFilters;
    }
  }, [showMoreFilters]);

  // Count active "more filters" (the secondary set behind Show more). When collapsed we surface
  // this count on the toggle so an applied-but-hidden filter isn't invisible (CR-291797692 rev 8).
  const activeSecondaryFilterCount = countActiveSecondaryFilters(filterValues);

  return (
    <div
      data-testid="top-search-filters"
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))',
        gap: '12px',
        alignItems: 'end',
      }}
    >
      {/* Primary filters (always visible) */}
      <div data-testid="filter-search" style={{ gridColumn: 'span 3' }}>
        <FormField label={t('topFilters.search')}>
          <Input
            value={filterValues.query}
            placeholder={t('topFilters.searchPlaceholder')}
            onChange={({ detail }) => {
              updateFilter('query', detail.value);
              clearTimeout(searchDebounceTimer.current);
              // 700ms debounce: avoids hammering the API on every keystroke.
              // 3-char minimum prevents overly broad searches that return the full catalog.
              // Clearing the field (length 0) triggers immediately so results reset without delay.
              searchDebounceTimer.current = setTimeout(() => {
                if (detail.value.length >= 3 || detail.value.length === 0) {
                  applyFiltersRef.current();
                }
              }, 700);
            }}
            type="search"
          />
        </FormField>
      </div>
      <div data-testid="filter-location" style={{ gridColumn: 'span 2' }}>
        <FormField label={t('topFilters.location')}>
          <Autosuggest
            value={locationLabel}
            onChange={({ detail }) => {
              setLocationLabel(detail.value);
              if (!detail.value) {
                updateFilters({ volunteer_location: '', volunteer_location_label: '' });
                applyImmediately();
              }
            }}
            onSelect={({ detail }) => handleLocationSelect(detail.value)}
            onLoadItems={({ detail }) => handleLocationLoadItems(detail.filteringText)}
            options={locationOptions}
            statusType={locationStatus}
            placeholder={t('topFilters.locationPlaceholder')}
            ariaLabel={t('topFilters.location')}
          />
        </FormField>
      </div>
      <div data-testid="filter-type" style={{ gridColumn: 'span 2' }}>
        <FormField label={t('topFilters.type')}>
          <Select
            selectedOption={
              activityTypeOptions.find((o) => o.value === filterValues.activity_location) || null
            }
            placeholder={t('topFilters.typePlaceholder')}
            options={activityTypeOptions}
            onChange={({ detail }) => {
              updateFilter('activity_location', detail.selectedOption.value || 'all');
              applyImmediately();
            }}
          />
        </FormField>
      </div>
      <div style={{ gridColumn: 'span 3' }}>
        <FormField label={t('topFilters.dateRange')}>
          <AbsoluteDatePicker
            value={getDateRangeFromStartAndEnd(
              filterValues.start_timestamp,
              filterValues.end_timestamp
            )}
            onChange={(value) => {
              const valueSet = value?.type === 'absolute';
              const startDate = valueSet ? value.startDate : '';
              const endDate = valueSet ? value.endDate : '';
              updateFilters({ start_timestamp: startDate, end_timestamp: endDate });
              applyImmediately();
            }}
          />
        </FormField>
      </div>

      {/* More filters (animated show/hide) */}
      <div
        ref={moreFiltersRef}
        data-testid="secondary-filters"
        style={{
          gridColumn: '1 / -1',
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))',
          gap: '12px',
          maxHeight: showMoreFilters ? '500px' : '0',
          overflow: showMoreFilters ? 'visible' : 'hidden',
          transition: 'max-height 0.3s ease-in-out',
        }}
      >
        <div style={{ gridColumn: 'span 2' }}>
          <FormField label={t('topFilters.daysOfWeek')}>
            <Multiselect
              inlineTokens
              selectedOptions={selectedDaysOfWeek}
              placeholder={t('topFilters.daysOfWeekPlaceholder')}
              options={daysOfWeekOptions}
              onChange={({ detail }) => {
                updateFilter('days_of_week', optionsToValueArrayString(detail.selectedOptions));
                applyImmediately();
              }}
            />
          </FormField>
        </div>
        <div style={{ gridColumn: 'span 1' }}>
          <FormField label={t('topFilters.minimumSpots')}>
            <Input
              value={filterValues.spots}
              placeholder={t('topFilters.minimumSpotsPlaceholder')}
              onChange={({ detail }) => {
                const val = detail.value;
                if (val === '' || (Number(val) >= 1 && Number.isInteger(Number(val)))) {
                  updateFilter('spots', val);
                  applyImmediately();
                }
              }}
              type="number"
              inputMode="numeric"
            />
          </FormField>
        </div>
        <div style={{ gridColumn: 'span 2' }}>
          <FormField label={t('topFilters.shiftLength')}>
            <Select
              selectedOption={
                filterValues.activity_length_min || filterValues.activity_length_max
                  ? shiftLengthOptions.find(
                      (o) =>
                        o.value ===
                        `${filterValues.activity_length_min || '0'}-${filterValues.activity_length_max || '999'}`
                    ) || null
                  : null
              }
              placeholder={t('topFilters.shiftLengthPlaceholder')}
              options={shiftLengthOptions}
              onChange={({ detail }) => {
                // Always write BOTH bounds, including the 0 / 999 sentinels. isValidShiftDuration
                // only narrows when activity_length_min AND _max are both set, so blanking a
                // sentinel silently disabled the "Under 1 hour" and "Over 5 hours" options
                // (CR-291797692 — the two open-ended ranges looked applied but filtered nothing).
                const { min, max } = shiftLengthValueToBounds(detail.selectedOption.value || '');
                updateFilters({ activity_length_min: min, activity_length_max: max });
                applyImmediately();
              }}
            />
          </FormField>
        </div>
        <div style={{ gridColumn: 'span 2' }}>
          <FormField label={t('topFilters.startsAfter')}>
            <Select
              selectedOption={
                filterValues.start_time
                  ? startTimeOptions.find((o) => o.value === filterValues.start_time) || null
                  : null
              }
              placeholder={t('topFilters.startsAfterPlaceholder')}
              options={startTimeOptions}
              onChange={({ detail }) => {
                updateFilter('start_time', detail.selectedOption.value || '');
                applyImmediately();
              }}
            />
          </FormField>
        </div>
        <div style={{ gridColumn: 'span 2' }}>
          <FormField label={t('topFilters.endsBefore')}>
            <Select
              selectedOption={
                filterValues.end_time
                  ? endTimeOptions.find((o) => o.value === filterValues.end_time) || null
                  : null
              }
              placeholder={t('topFilters.endsBeforePlaceholder')}
              options={endTimeOptions}
              onChange={({ detail }) => {
                updateFilter('end_time', detail.selectedOption.value || '');
                applyImmediately();
              }}
            />
          </FormField>
        </div>
      </div>

      {/* Toggle + Clear buttons */}
      <div
        style={{
          display: 'flex',
          gap: '8px',
          alignItems: 'end',
          gridColumn: '1 / -1',
          whiteSpace: 'nowrap',
        }}
      >
        <Button
          variant="link"
          data-testid="toggle-more-filters"
          onClick={() => setShowMoreFilters(!showMoreFilters)}
        >
          {showMoreFilters
            ? `- ${t('topFilters.showLessFilters')}`
            : `+ ${t('topFilters.showMoreFilters')}${
                activeSecondaryFilterCount > 0 ? ` (${activeSecondaryFilterCount})` : ''
              }`}
        </Button>
        <Button variant="link" onClick={resetFilters} data-aci-analytics-name="filter_resetFilters">
          {t('topFilters.clearFilters')}
        </Button>
      </div>
    </div>
  );
}
